import { OptionallyVisible } from 'components';
import { useNotifications } from 'providers/notifications/useNotifications';
import { useTranslation } from 'react-i18next';
import { useCancelCryptoPayoutMutation, useLazyGetPayoutReceiptQuery } from 'services/cryptoPayouts';
import { useGetMerchantQuery } from 'services/merchants';

import { Panel } from 'components/containers';
import { Delimiter } from 'components/delimiter';
import Link from 'components/link';

import { CryptoPayoutStatusBadge } from 'pages/cryptoPayout/components/statusBadge';

import RoutePath from 'router/path';

import { CryptoPayout } from 'types/cryptoPayout';
import { ReduxToolkitError } from 'types/http';
import { NotificationSeverity } from 'types/notifications';

import { formatDateLocal } from 'utils/date';
import downloadFile from 'utils/downloadFile';
import { formatFiatCurrencyI18n } from 'utils/i18n';

import LABELS, { KEYS } from './keys';
import { Item, ItemName, ItemValue, SecondaryItemValue, SectionWrapper, StyledButton } from './styles';

interface CryptoPayoutDetailsSectionProps {
  payout: CryptoPayout;
  showMerchantName?: boolean;
  refetch: () => void;
}

const CryptoPayoutDetailsSection = ({ payout, showMerchantName, refetch }: CryptoPayoutDetailsSectionProps) => {
  const { t, i18n } = useTranslation();
  const isReceiptAvailable = KEYS.SUCCESSFUL_STATUSES.includes(payout.status);
  const { pushToast } = useNotifications();
  const [getReceipt, { isFetching: isFetchingReceipt }] = useLazyGetPayoutReceiptQuery();
  const formatFiatValue = formatFiatCurrencyI18n(i18n.language);
  const { data: merchantResponse, error: merchantError } = useGetMerchantQuery(payout.merchantUuid, {
    skip: !payout.merchantUuid,
  });
  const [cancel, { isLoading: isCancelling }] = useCancelCryptoPayoutMutation();
  const isCancelAvailable = payout.isCancelable;

  const onGetPDFClick = async () => {
    const { uuid } = payout;

    if (!uuid) {
      return;
    }

    try {
      const result = await getReceipt(uuid).unwrap();
      if (result.type !== 'application/pdf') {
        throw new Error('Unexpected file type');
      }

      const objectUrl = window.URL.createObjectURL(result);
      downloadFile({
        name: t(LABELS.PDF_FILE_NAME, { uuid }),
        source: objectUrl,
      });
    } catch (e: any) {
      pushToast({
        severity: NotificationSeverity.error,
        message: t(LABELS.PDF_DOWNLOAD_ERROR),
      });
    }
  };

  const onCancelClick = async () => {
    const { uuid } = payout;

    if (!uuid) {
      return;
    }

    const result = await cancel({ uuid });
    if ('error' in result) {
      pushToast({
        severity: NotificationSeverity.error,
        message: t(LABELS.CANCEL_ERROR),
      });
      return;
    }

    refetch();
  };

  return (
    <Panel label={t(LABELS.TITLE)}>
      <SectionWrapper>
        <OptionallyVisible visible={isReceiptAvailable}>
          <Item>
            <ItemName>{t(LABELS.RECEIPT)}</ItemName>
            <ItemValue>
              <StyledButton secondary onClick={onGetPDFClick} isLoading={isFetchingReceipt}>
                {t(LABELS.DOWNLOAD)}
              </StyledButton>
            </ItemValue>
          </Item>
        </OptionallyVisible>
        <Item>
          <ItemName>{t(LABELS.UUID)}</ItemName>
          <SecondaryItemValue>{payout.uuid}</SecondaryItemValue>
        </Item>
        <Item>
          <ItemName>{t(LABELS.EXTERNAL_PAYOUT_ID)}</ItemName>
          <SecondaryItemValue>{payout.externalPayoutId ?? '-'}</SecondaryItemValue>
        </Item>
        <OptionallyVisible visible={Boolean(showMerchantName)}>
          <Item>
            <ItemName>{t(LABELS.MERCHANT_NAME)}</ItemName>
            <ItemValue>
              <Link to={RoutePath.pwc.merchantById(merchantResponse?.data?.uuid || '')}>
                {merchantResponse?.data.name ?? (merchantError as ReduxToolkitError)?.data?.errorMessage}
              </Link>
            </ItemValue>
          </Item>
        </OptionallyVisible>
        <Item>
          <ItemName>{t(LABELS.CREATION_TIME)}</ItemName>
          <ItemValue>{formatDateLocal(payout.createdAt)}</ItemValue>
        </Item>
        <Item>
          <ItemName>{t(LABELS.STATUS)}</ItemName>
          <ItemValue>
            <CryptoPayoutStatusBadge status={payout.status} />
            <OptionallyVisible visible={isCancelAvailable}>
              <StyledButton secondary onClick={onCancelClick} isLoading={isCancelling}>
                {t(LABELS.CANCEL)}
              </StyledButton>
            </OptionallyVisible>
          </ItemValue>
        </Item>
        <Delimiter />
        <Item>
          <ItemName>{t(LABELS.AMOUNT)}</ItemName>
          <ItemValue>{formatFiatValue(payout.fiatAmount, KEYS.HARDCODED_CURRENCY)}</ItemValue>
        </Item>
        <Delimiter />
        <Item>
          <ItemName>{t(LABELS.PROCESSING_FEE)}</ItemName>
          <ItemValue>{`${payout.processingFeePercent} %`}</ItemValue>
        </Item>
        <OptionallyVisible visible={Boolean(payout.processingFeeFiatAmount)}>
          <Item>
            <ItemName>{t(LABELS.PROCESSING_FREE_AMOUNT)}</ItemName>
            <ItemValue>{formatFiatValue(payout.processingFeeFiatAmount!, KEYS.HARDCODED_CURRENCY)}</ItemValue>
          </Item>
        </OptionallyVisible>
        <OptionallyVisible visible={payout.networkFee !== undefined}>
          <Item>
            <ItemName>{t(LABELS.NETWORK_FEE)}</ItemName>
            <ItemValue>{formatFiatValue(Number(payout.networkFee ?? 0), KEYS.HARDCODED_CURRENCY)}</ItemValue>
          </Item>
        </OptionallyVisible>
      </SectionWrapper>
    </Panel>
  );
};

export default CryptoPayoutDetailsSection;
